import type { IconProps } from '@openeducation/oe-rn-shared-types';
import { styled } from '@openeducation/oe-rn-themes';
import React, { useRef } from 'react';
import type { StyleProp, View, ViewStyle } from 'react-native';
import { useHover } from 'react-native-web-hooks';

import { CardIcon } from './card-icon';
import { CardRecommendedMark } from './card-recommended-mark';

export interface CardProps {
  selected?: boolean;
  recommendedText?: string;
  icon?: (props: IconProps) => JSX.Element;
  style?: StyleProp<ViewStyle>;
  onPress?: () => void;
  children: React.ReactNode;
}

export function Card({
  selected,
  recommendedText,
  icon,
  style,
  onPress,
  children,
}: CardProps) {
  const cardRef = useRef<View>(null);
  const isHovered = useHover(cardRef);

  return (
    <StyledCard
      ref={cardRef}
      hovered={isHovered}
      style={style}
      recommended={!!recommendedText}
      selected={selected}
      onPress={onPress}
    >
      {icon && <CardIcon selected={selected} hovered={isHovered} icon={icon} />}
      {recommendedText && (
        <CardRecommendedMark>{recommendedText}</CardRecommendedMark>
      )}
      {children}
    </StyledCard>
  );
}

const StyledCard = styled.Pressable<{
  selected?: boolean;
  recommended?: boolean;
  hovered: boolean;
}>`
  border: 1px solid;
  border-color: ${({ theme, selected, hovered }) =>
    selected || hovered ? theme.color.primary : theme.color.backgroundDarkGray};
  border-radius: 8px;
  border-top-right-radius: ${({ recommended }) => (recommended ? '0' : '8px')};
  shadow-color: ${({ theme }) => theme.color.border};
  shadow-radius: 8px;
  background-color: ${({ theme, selected }) =>
    selected ? theme.color.lightPrimary : theme.color.white};
`;
